import { Box, Divider, styled, Tooltip, Typography } from '@mui/material';
import Button from 'components/Button';
import { Column, Row } from 'components/Layout';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import { round } from 'lodash';

const Container = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  padding: theme.spacing(theme.spacingSizes.sm, theme.spacingSizes.lg),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.spacingSizes.xs),
  width: '100%',
  backgroundColor: theme.palette.text.white,
  maxWidth: '630px',
}));

const Label = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const Value = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const Info = styled(Box)({
  width: '100%',
});

const InfoButton = styled(Button)(({ theme }) => ({
  borderBottom: `1px dashed ${theme.palette.primary.main}`,
  padding: 0,
  fontSize: '14px',
}));

type Props = {
  saleAmount: number;
  purchaseAmount: number;
  rootCurrency: CurrencyCode;
  saleRate: number;
  purchaseRate: number;
  foreignCurrencyGain: number;
  className?: string;
};

const tooltipText =
  "To calculate the cost of foreign currency, the Further system uses the average cost method. This method smooths out fluctuations in the exchange rate by calculating the average rate at which the relevant currency was acquired over the vehicle's lifetime. This method provides a balanced approach, mitigating the effects of short-term exchange rate volatility in reporting the cost of foreign currency for a particular transaction.";

const CurrencySaleReview: React.FC<Props> = ({
  rootCurrency,
  saleAmount,
  purchaseAmount,
  className,
  saleRate,
  purchaseRate,
  foreignCurrencyGain,
}) => {
  const costsAmount = purchaseRate ? round(saleAmount / purchaseRate, 2) : 0;

  const summaryAmountText = numberToCurrencyString(
    Math.abs(foreignCurrencyGain),
    {
      currency: rootCurrency,
    },
  );

  return (
    <Column fullWidth spacing="sm" className={className} centered>
      <Container className={className}>
        <Row centered fullWidth justify="space-between" spacing="sm">
          <Label variant="body2">
            Sale value of foreign currency in {rootCurrency} (FX sale rate:{' '}
            {round(saleRate, 6).toFixed(6)})
          </Label>
          <Value variant="body2">
            {numberToCurrencyString(purchaseAmount, {
              currency: rootCurrency,
            })}
          </Value>
        </Row>
        <Row centered fullWidth justify="space-between" spacing="sm">
          <Label variant="body2">
            Cost of foreign currency in {rootCurrency} (average FX purchase
            rate: {round(purchaseRate, 6).toFixed(6)})
          </Label>
          <Value variant="body2">
            {numberToCurrencyString(costsAmount, {
              currency: rootCurrency,
            })}
          </Value>
        </Row>
        <Divider />
        <Row centered fullWidth justify="space-between" spacing="sm">
          <Label variant="body2" fontWeight={700}>
            Foreign currency gain/(loss)
          </Label>
          <Value variant="body2" fontWeight={700}>
            {foreignCurrencyGain < 0
              ? `(${summaryAmountText})`
              : summaryAmountText}
          </Value>
        </Row>
      </Container>
      <Info>
        <Tooltip title={tooltipText} arrow small>
          <InfoButton variant="text" color="primary">
            How is the average FX purchase rate calculated?
          </InfoButton>
        </Tooltip>
      </Info>
    </Column>
  );
};

export default CurrencySaleReview;
